export type TrainingType = "customer_service" | "sales";

export type CampaignStatus = "draft" | "active" | "archived";

export type SessionStatus =
    | "invited"
    | "started"
    | "in_progress"
    // "scoring": conversation finished, BE is grading the attempt. Surfaced by
    // the attempt history once the roleplay ends; treated as a terminal/used-up
    // state (the trainee can't resume it - they get a fresh attempt instead).
    | "scoring"
    | "completed"
    | "failed"
    | "expired"
    | "abandoned";

// Present a BE session status verbatim - split on "_" and capitalise each word
// ("started" → "Started", "in_progress" → "In Progress"). Per BE, the FE must
// NOT remap statuses (e.g. "started" must not read "In Progress"); the BE owns
// the status vocabulary, so this is formatting only, never translation.
export function formatSessionStatus(status: string): string {
    return status
        .split("_")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(" ");
}

export interface TrainingPersona {
    id: string;
    name: string;
    training_type: TrainingType;
    description: string;
    briefing: string;
    assessment_id?: string;
    voice_key?: string;
    gender?: "male" | "female";
    // Short language code stored on the persona in the DB (e.g. "en", "id", "my", "vn").
    // Resolved to a LANGUAGES[].value via resolveLanguage() to pick the setup/static audio.
    language?: string | null;
    max_duration_seconds?: number | null;
}

export interface RubricScores {
    empathy: number;
    problem_resolution: number;
    product_knowledge: number;
    tone: number;
    call_handling: number;
}

export interface BeSpeechMetrics {
    words_per_minute: number;
    total_employee_words: number;
    total_employee_speaking_seconds: number;
    filler_count: number;
    filler_per_minute: number;
    filler_breakdown: Record<string, number>;
    longest_monologue_seconds: number;
    talk_listen_ratio: {
        employee_pct: number;
        persona_pct: number;
    };
}

export interface BeFeedbackItem {
    category: "Brilliant" | "Excellent" | "Good" | "Inaccuracy" | "Mistake";
    title: string;
    quote: string;
    content: string;
    timestamp: string;
    // Points this moment cost the overall score — only meaningful for negative
    // moments (Inaccuracy/Mistake). The BE emits a holistic score with NO
    // per-moment deductions today, so this is absent and the FE shows no tag
    // until it lands. See handoff-docs/18-06-2026-training-score-deduction.md.
    points_lost?: number;
    // KB document this item's correction is backed by (Phase 3, #3584). The BE
    // only sets it when the scorer cited a document that actually fed this
    // score — it validates the id against the result's knowledge_manifest and
    // drops unknown/fabricated ids (pipecat.py:682), so a present value is a
    // trustworthy claim. Absent when the item isn't backed by a specific doc.
    source_document_id?: string | null;
}

// Prioritised remediation item for the roleplay results Overview tab — mirrors
// the AIQM ActionItem shape (src/types/aiqm.ts) so the Overview matches AIQM
// field-for-field. BACKEND: not yet emitted for roleplay sessions — see the
// handoff doc handoff-docs/18-06-2026-training-results-overview-fields.md.
export interface TrainingActionItem {
    priority: "high" | "medium" | "low";
    description: string;
    category: string;
    technical_suggestion: string;
    location: string;
}

// ---- Knowledge manifest (BE: Trainer KB scoping Phase 0) ----
// Snapshot, persisted at scoring time, of exactly which Knowledge Base
// documents were provided to the AI evaluator for one result. This is the only
// honest source for a "knowledge used" display: it reflects what the scoring
// prompt actually contained, not what documents exist now. Snake_case because
// results payloads are passed through unmapped.
export type KnowledgeScopeType = "workspace" | "campaign" | "aiqm_batch";

export interface KnowledgeManifestDoc {
    document_id: string;
    name: string;
    scope: KnowledgeScopeType;
    /** Content hash of the exact bytes used — the audit anchor. */
    sha256?: string | null;
    /** True when this document's text made it into the evaluation context. */
    included: boolean;
    /** True when only part of the text fit the context budget. */
    truncated?: boolean;
    chars_included?: number | null;
}

export interface KnowledgeManifest {
    documents: KnowledgeManifestDoc[];
    context_chars_used?: number | null;
    context_chars_budget?: number | null;
    generated_at?: string | null;
}

export interface TrainingSession {
    id: string;
    campaign_id: string;
    employee_email: string;
    employee_name: string;
    token: string;
    attempt_number: number;
    status: SessionStatus;
    started_at: string | null;
    completed_at: string | null;
    rubric_scores: RubricScores | null;
    total_score: number | null;
    qualitative_feedback: string | null;
    feedback_strengths?: string | null;
    feedback_growth?: string | null;
    transcript_highlight?: { timestamp: string; quote: string; coach_note: string } | null;
    transcript?: TranscriptTurn[] | null;
    transcript_full?: TranscriptTurn[] | null;
    employee_role?: string | null;
    employee_department?: string | null;
    is_test?: boolean;
    // BE-supplied feedback fields (per TRAINING_FEEDBACK_BE_2026-05-06 / FE
    // handoff 2026-05-07). Optional so existing mocks and pre-cutover
    // responses still typecheck.
    speech_metrics?: BeSpeechMetrics | null;
    feedback_items?: BeFeedbackItem[] | null;
    recording_url?: string | null;
    expires_at?: string | null;
    // BE-supplied session metadata: session_duration is the roleplay length in
    // minutes; session_expired_at is the link-expiry timestamp (ISO). Both are
    // sourced from apiGetSession and preferred over persona/expires_at fallbacks.
    session_duration?: number | null;
    session_expired_at?: string | null;
    campaign_name?: string | null;
    persona_name?: string | null;
    team_leader?: string | null;
    // Overview-tab fields that bring the roleplay results page to parity with the
    // AIQM detail Overview. BACKEND: not emitted for roleplay sessions yet — the
    // FE renders graceful empty states until these land (see handoff doc
    // handoff-docs/18-06-2026-training-results-overview-fields.md).
    conversation_flow_assessment?: string | null;
    action_items?: TrainingActionItem[] | null;
    // Knowledge documents the evaluator was actually given for THIS result
    // (BE: Trainer KB scoping Phase 0). Absent on legacy results scored before
    // the manifest landed; required non-null on freshly scored sessions.
    knowledge_manifest?: KnowledgeManifest | null;
}

export interface TrainingCampaign {
    id: string;
    name: string;
    training_type: TrainingType;
    persona_id: string;
    persona_name?: string | null;
    // Concise scenario blurb shown under the persona name in the campaign table.
    // Optional: BE may not send it yet; FE falls back to the static persona catalog.
    persona_description?: string | null;
    status: CampaignStatus;
    created_at: string;
    employee_count: number;
    completed_count: number;
    avg_score: number | null;
    insights_headline?: string | null;
    team_leader?: string | null;
    // Language of the campaign. Matches LANGUAGES[].value from src/types/languages.ts.
    // Used to select the correct static intro audio on the Setup Check page.
    // Defaults to "en-US" when absent.
    language?: string | null;
}

export interface TrainingCampaignDetail extends TrainingCampaign {
    sessions: TrainingSession[];
}

export interface EmployeeRow {
    name: string;
    email: string;
    role?: string;
    department?: string;
    team_leader?: string;
}

export interface TranscriptTurn {
    speaker: "employee" | "persona";
    text: string;
    ts: string;
}

export const RUBRIC_LABELS: Record<keyof RubricScores, string> = {
    empathy: "Empathy",
    problem_resolution: "Problem Resolution",
    product_knowledge: "Product Knowledge",
    tone: "Tone",
    call_handling: "Call Handling",
};

export const TRAINING_TYPE_LABEL: Record<TrainingType, string> = {
    customer_service: "Customer Service",
    sales: "Sales",
};

// --- Rubric ---

export interface RubricCriterion {
    id: string;
    name: string;
    weight: number;
    description: string;
    correct_answer: string;
    scoring_guide: string;
    tip?: string;
}

// A single scoring category in a rubric template. This is the source of truth
// the editor reads from: `goodAnswerOptions` become the "What a good answer
// looks like" checkboxes and `scoringGuide` seeds the High/Mid/Low textareas.
// Keep the high/mid/low text free of any "High score -" style prefix - the
// editor adds the label itself, and the prefix would otherwise leak into the
// response body.
export interface RubricCategoryTemplate {
    id: keyof RubricScores;
    name: string;
    description: string;
    defaultWeight: number;
    goodAnswerOptions: string[];
    scoringGuide: { high: string; mid: string; low: string };
    tip?: string;
}

// --- Client rubric: Superbank live-chat QA (Bahasa Indonesia) ---
//
// Adapted from a client's live-chat QA scorecard. NOT a public TrainingType -
// it is gated to Superbank's company_id (see @constant/company). It seeds the
// AIQM upload dialog automatically for Superbank users, and is Superbank's
// Customer Service default in the campaign wizard. Pass SUPERBANK_LIVECHAT_RUBRIC
// as the editor's `value` and SUPERBANK_LIVECHAT_TEMPLATE as its `optionsTemplate`
// so the Indonesian good-answer options render as checkboxes.
//
// The source scorecard has 8 positively-weighted parameters (summing to 100)
// PLUS structure our positive-only model cannot represent, so the following are
// intentionally FLATTENED / DROPPED here:
//   - Fatal vs Non-Fatal parameter flags (no pass/fail concept)
//   - Penalty modifiers: Robotic (-50), Zero Tolerance (-100)  -> folded into
//     `tone` / `problem_resolution` as "must-not" guidance, losing their
//     score-crushing severity
//   - Bonus: Complimentary (+5%)
// Faithful modelling of those needs BE scoring support (see FE->BE handoff).
//
// Source -> category mapping (weights re-summed to 100):
//   empathy(5)            <- empathy bullet of Opening & Greeting
//   problem_resolution(45)<- Analysis(15) + Validation(20) + Solution half of SOP & Solution
//   product_knowledge(30) <- Assurance(15) + SOP half of SOP & Solution + Ticketing(7)
//   tone(10)              <- Communication(5) + Robotic-as-"not scripted"
//   call_handling(10)     <- Opening/Greeting/Closing structure + Maintain Customer (response time/hold)
export const SUPERBANK_LIVECHAT_TEMPLATE: RubricCategoryTemplate[] = [
    {
        id: "empathy",
        name: "Empati",
        description:
            "Memberikan empati yang tulus dan relevan dengan pertanyaan, permintaan, atau keluhan Nasabah, serta menyapa Nasabah secara personal.",
        defaultWeight: 5,
        goodAnswerOptions: [
            "Memberikan empati yang sesuai dengan pertanyaan, permintaan, atau keluhan Nasabah dengan kalimat yang benar dan tepat",
            "Mengakui atau menanggapi keluhan Nasabah, tidak hanya diam",
            "Menyebut nama Nasabah secara merata sepanjang interaksi (minimal 3x dalam 1 interaksi)",
            "Menunjukkan perhatian penuh dan memahami kebutuhan Nasabah",
        ],
        scoringGuide: {
            high: "Konsisten memberikan empati yang tulus dan relevan dengan situasi Nasabah, menyapa nama Nasabah secara merata, dan menunjukkan pemahaman atas kebutuhan Nasabah sepanjang interaksi.",
            mid: "Memberikan empati namun terlambat, terkesan generik, atau hanya muncul di sebagian interaksi; penyebutan nama Nasabah kurang merata.",
            low: "Tidak memberikan empati yang tulus, tidak mengakui keluhan Nasabah, atau langsung ke pertanyaan/fakta sehingga Nasabah merasa tidak didengar.",
        },
        tip: "Akui perasaan Nasabah sebelum masuk ke solusi, dan sebut namanya agar interaksi terasa personal.",
    },
    {
        id: "problem_resolution",
        name: "Penyelesaian Masalah",
        description:
            "Memahami case Nasabah dengan tepat, memastikan kelengkapan data sebelum eskalasi, dan memberikan solusi yang jelas dan lengkap hingga tuntas.",
        defaultWeight: 45,
        goodAnswerOptions: [
            "Memahami dengan tepat pertanyaan/permintaan/keluhan Nasabah tanpa menebak atau berasumsi",
            "Tanggap mengidentifikasi case Nasabah dan melakukan probing bila informasi pendukung belum lengkap",
            "Memastikan kelengkapan data dan dokumen sudah sesuai sebelum eskalasi ke unit terkait",
            "Memberikan solusi yang tepat, jelas, dan lengkap sesuai pertanyaan/permintaan/keluhan Nasabah",
            "Mengonfirmasi tiket komplain/permintaan dengan tanggal pengaduan, nama agent, case, SLA, dan nomor tiket",
            "Mengambil kepemilikan penuh atas case hingga selesai, bukan mengoper Nasabah antar unit",
        ],
        scoringGuide: {
            high: "Memahami case dengan tepat tanpa berasumsi, melakukan probing bila perlu, memastikan kelengkapan data dan dokumen sebelum eskalasi, memberikan solusi yang tepat dan lengkap, serta mengonfirmasi tiket dengan detail (tanggal, agent, case, SLA, nomor tiket).",
            mid: "Mencapai solusi yang memadai namun melewatkan satu langkah, seperti probing yang kurang dalam, kelengkapan dokumen yang belum dipastikan, atau detail konfirmasi tiket yang tidak lengkap.",
            low: "Gagal memahami atau menebak case Nasabah, tidak melakukan probing, meninggalkan case tanpa solusi yang jelas, atau tidak memastikan kelengkapan data sebelum eskalasi.",
        },
        tip: "Pastikan data dan dokumen lengkap sebelum eskalasi, dan konfirmasi nomor tiket agar Nasabah punya referensi yang jelas.",
    },
    {
        id: "product_knowledge",
        name: "Pengetahuan Produk & SOP",
        description:
            "Menyampaikan hasil pengecekan data secara akurat dan meyakinkan, mengikuti SOP yang benar, dan mencatat tiket sesuai ketentuan.",
        defaultWeight: 30,
        goodAnswerOptions: [
            "Menyampaikan hasil pengecekan data dengan benar, lengkap, dan detail sehingga meyakinkan Nasabah",
            "Melayani dengan percaya diri, tidak ragu-ragu, dan tidak terdengar bingung",
            "Melayani Nasabah sesuai SOP yang benar dan memilih SR yang tepat (L1)",
            "Mengedukasi Nasabah mengenai cara cepat/self-service untuk kebutuhan mereka",
            "Mencatat deskripsi tiket sesuai macro pencatatan dan mengonfirmasi tiket sesuai ketentuan",
        ],
        scoringGuide: {
            high: "Menyampaikan hasil pengecekan data secara akurat dan meyakinkan, melayani dengan percaya diri sesuai SOP yang benar, memilih SR yang tepat, mengedukasi Nasabah, dan mencatat tiket sesuai macro.",
            mid: "Informasi sebagian besar benar namun ragu pada SOP, pemilihan SR, atau detail pencatatan tiket tertentu.",
            low: "Menyampaikan informasi yang salah, mengikuti SOP yang keliru, salah memilih SR, atau tidak mencatat tiket sesuai ketentuan.",
        },
        tip: "Bila belum yakin, konfirmasi dulu daripada menebak - informasi yang akurat lebih menenangkan Nasabah daripada jawaban cepat yang salah.",
    },
    {
        id: "tone",
        name: "Komunikasi & Gaya Bahasa",
        description:
            "Menggunakan ejaan, tanda baca, dan bahasa yang profesional serta ramah, dengan kalimat terstruktur dan bukan jawaban template.",
        defaultWeight: 10,
        goodAnswerOptions: [
            "Menggunakan huruf kapital, tanda baca, dan spasi dengan tepat (toleransi maksimal 3x)",
            "Tidak melakukan typo atau singkatan kata (toleransi maksimal 3x)",
            "Tidak menggunakan negative word atau kata-kata yang terkesan ragu-ragu",
            "Menggunakan bahasa yang profesional, ramah, dan kalimat yang terstruktur dengan baik",
            "Tidak memberikan jawaban template/copy-paste; mengecek histori interaksi dan tiket Nasabah sebelumnya",
        ],
        scoringGuide: {
            high: "Menggunakan ejaan, tanda baca, dan bahasa yang profesional serta ramah tanpa typo berlebihan, kalimat terstruktur, dan tidak memberikan jawaban yang bersifat template/robotic.",
            mid: "Secara umum profesional namun sesekali typo/singkatan melewati toleransi, memakai kata ragu-ragu, atau jawaban terasa kurang personal.",
            low: "Banyak typo/singkatan, menggunakan negative word atau kata ragu-ragu, bahasa kurang profesional, atau memberikan jawaban template/copy-paste tanpa mengecek histori Nasabah.",
        },
        tip: "Cek histori interaksi Nasabah sebelum menjawab agar respon terasa personal dan tidak seperti template.",
    },
    {
        id: "call_handling",
        name: "Penanganan Interaksi",
        description:
            "Mengelola alur live chat dari greeting hingga closing sesuai template, serta menjaga responsivitas agar Nasabah tidak menunggu lama.",
        defaultWeight: 10,
        goodAnswerOptions: [
            "Melakukan standard greeting sesuai script, menyapa nama Nasabah, dan menawarkan bantuan awal",
            "Merespon live chat Nasabah tidak lebih dari 1 menit",
            "Melakukan maintain/hold dengan benar (maintain per 1 menit; hold hingga 5 menit khusus tiket request/complaint atau validasi dokumen)",
            "Menggunakan magic word pada interaksi",
            "Melakukan closing sesuai template (greeting akhir, CSAT, dan footer bahaya penipuan)",
        ],
        scoringGuide: {
            high: "Membuka dengan greeting standar sesuai script dan menawarkan bantuan, merespon dalam 1 menit, melakukan maintain/hold sesuai ketentuan, dan menutup interaksi sesuai template (greeting akhir, CSAT, footer penipuan).",
            mid: "Melalui tahapan utama namun ada yang kurang lengkap, seperti greeting tanpa penawaran bantuan, respon sedikit melebihi 1 menit, atau closing yang melewatkan salah satu komponen template.",
            low: "Melewatkan tahap inti: membuka tanpa greeting yang benar, tidak merespon/maintain Nasabah lebih dari 1 menit, atau mengakhiri interaksi tanpa closing yang sesuai.",
        },
        tip: "Jaga respon di bawah 1 menit dan bila perlu waktu, kirim konfirmasi keaktifan agar Nasabah tidak merasa diabaikan.",
    },
];

// Per-training-type rubric templates. The 5 category ids are fixed (the BE
// scores against them via RubricScores), but the descriptions, good-answer
// options, and scoring guides are tailored to each training type. Adding a new
// training type is just adding a new entry here - the wizard pre-selects the
// matching template from the type chosen in step 2.
export const RUBRIC_TEMPLATES: Record<TrainingType, RubricCategoryTemplate[]> = {
    customer_service: [
        {
            id: "empathy",
            name: "Empathy",
            description: "Understanding and acknowledging the customer's feelings and situation with genuine care and compassion.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Acknowledges the customer's concern immediately after they state their issue",
                "Delivers a sincere, relevant empathy or apology statement when needed (e.g. 'I apologize for any inconvenience caused')",
                "Gives full attention with active listening and shows they understand the customer's needs",
                "Comments on or asks about the customer's concern rather than staying silent",
            ],
            scoringGuide: {
                high: "Acknowledges the concern the moment it is raised and consistently expresses sincere, well-placed empathy or apology, listening actively and reflecting a clear understanding of the customer's needs throughout.",
                mid: "Acknowledges the concern and offers some empathy, but it arrives late, sounds generic or scripted, or only appears at one or two points rather than across the whole interaction.",
                low: "Offers no genuine empathy or apology and fails to acknowledge the concern, moving straight to questions or facts while the customer feels unheard.",
            },
            tip: "Acknowledge the feeling before going to the fix, because a simple 'I can see why that's frustrating' shifts the entire tone of the call.",
        },
        {
            id: "problem_resolution",
            name: "Problem Resolution",
            description: "Effectively identifying the customer's issue and providing a clear, actionable solution that resolves their concern.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Probes to clarify the concern and checks previous cases or records before acting",
                "Collects all the information needed to resolve or escalate the case",
                "Provides a clear solution and informs the customer of the overall case status (e.g. case closure)",
                "Provides the case or ticket number for the customer's reference",
                "Takes full ownership of the concern instead of a 'quick kill' or passing the customer between departments",
            ],
            scoringGuide: {
                high: "Probes thoroughly and checks existing cases before acting, resolves the concern with a clear solution, keeps the customer updated on the overall case status, shares the case or ticket reference, and owns the issue from start to finish.",
                mid: "Reaches a workable resolution but skips a step along the way, such as a deeper probe, an update on the case status, or providing the ticket reference, leaving part of the outcome unclear.",
                low: "Fails to probe or check existing cases, leaves the concern unresolved or vague, or hands the customer off to another department instead of taking ownership.",
            },
            tip: "Before closing, confirm the solution works for them. A quick 'Does that sound okay to you?' makes the resolution feel complete rather than assumed.",
        },
        {
            id: "product_knowledge",
            name: "Product Knowledge",
            description: "Demonstrating accurate and confident knowledge of products, services, policies, and procedures.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Gives correct information on products, services, and feedback timelines (SLA)",
                "Follows the correct process, procedure, and policy for the situation",
                "Provides accurate reference details where relevant (address, contact number, operating hours, website, or reference number)",
                "Educates the customer and sets realistic expectations on timelines and outcomes",
                "Escalates to the correct team or person when the issue is beyond their authority",
            ],
            scoringGuide: {
                high: "Gives accurate information on products, services, and SLAs, follows the correct process and policy, supplies the right reference details, educates the customer on what to expect, and escalates to the correct team only when needed.",
                mid: "Provides largely correct information but is vague or uncertain on a specific SLA, policy, or reference detail, or sets expectations that are only partially clear.",
                low: "Shares incorrect information, follows the wrong process or policy, omits key reference details, or fails to educate the customer on the outcome or next steps.",
            },
            tip: "When unsure, give an honest timeline rather than guessing. Saying 'Let me confirm that for you' builds more trust than a wrong answer delivered confidently.",
        },
        {
            id: "tone",
            name: "Tone",
            description: "Maintaining a professional, friendly, and appropriately warm tone throughout the conversation.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Speaks at a natural, unhurried pace without interrupting or talking over the customer",
                "Uses positive, professional language with no slang or fillers and avoids negative phrases like 'no' or 'cannot'",
                "Keeps an engaged, warm intonation that is not monotone, robotic, or canned",
                "Avoids dead air or silence longer than 5 seconds, keeping the customer informed",
                "Checks the customer's preferred language before switching (e.g. 'May I continue in <language>?')",
            ],
            scoringGuide: {
                high: "Maintains a warm, natural pace without interrupting, uses positive and professional language free of slang, fillers, or negative phrasing, and keeps the customer informed with no noticeable dead air.",
                mid: "Stays professional overall but occasionally sounds monotone or rushed, slips in the odd filler or jargon term, or leaves a short silence before responding.",
                low: "Comes across as robotic or rushed, talks over the customer, relies on slang, jargon, or negative phrasing such as 'no' or 'cannot', or leaves long silences and dead air.",
            },
            tip: "Stay calm and warm even if the customer raises their voice, because your composure sets the temperature of the entire conversation.",
        },
        {
            id: "call_handling",
            name: "Call Handling",
            description: "Managing the conversation flow effectively, including active listening, pacing, and a positive close.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Opens with a complete greeting including a branded introduction and an offer to assist (e.g. 'Good morning, thank you for calling [Company], how may I assist you?')",
                "Verifies the caller as the genuine party before discussing the case (name, contact number, email, and account or address where required)",
                "Follows proper hold procedure: asks permission, gives a reason and time (max 2 minutes), and thanks the customer on return",
                "Closes with the key elements: a recap, an offer of further assistance, any required survey, and a professional closing (e.g. 'Thank you for calling [Company]')",
            ],
            scoringGuide: {
                high: "Opens with a complete greeting that includes a branded introduction and an offer to assist, verifies the caller correctly before discussing the case, follows proper hold procedure, and closes with a recap, an assistance offer, any required survey, and a professional closing.",
                mid: "Moves through the main stages but leaves one incomplete, such as a greeting missing a component, a hold without a thank-you, or a close that skips the recap or survey.",
                low: "Skips or fumbles a core stage, opening without a proper greeting, proceeding without verification, or ending the interaction without a proper close.",
            },
            tip: "Wrap up with a clear summary of what was agreed and check the customer is satisfied, because a strong close leaves a lasting impression.",
        },
    ],
    // General Sales template - company-agnostic, reusing the 5 fixed category
    // ids with sales-oriented content. Drafted for review; copy can be tuned.
    sales: [
        {
            id: "empathy",
            name: "Empathy",
            description: "Building rapport and genuinely understanding the customer's needs, goals, and pain points before pitching.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Opens by building rapport before moving into a pitch",
                "Asks discovery questions to understand the customer's needs, goals, and pain points",
                "Acknowledges the customer's situation and reflects it back before proposing a solution",
                "Listens actively and lets the customer speak without rushing or interrupting",
            ],
            scoringGuide: {
                high: "Builds genuine rapport, uncovers the customer's needs through thoughtful discovery, and tailors the conversation to what the customer actually cares about.",
                mid: "Asks some discovery questions but begins pitching before fully understanding the customer's needs.",
                low: "Launches into a pitch with little or no discovery or rapport, treating every customer the same.",
            },
            tip: "Discover before you pitch, because a solution only lands when the customer feels you understand their problem first.",
        },
        {
            id: "problem_resolution",
            name: "Problem Resolution",
            description: "Matching the product or service to the customer's needs and addressing objections with clear, relevant solutions.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Connects product features to the specific needs the customer raised",
                "Addresses objections directly with relevant, honest answers rather than deflecting",
                "Proposes a clear recommendation and explains why it fits the customer",
                "Confirms the recommendation meets the customer's needs before moving on",
            ],
            scoringGuide: {
                high: "Ties the recommendation directly to the customer's stated needs, handles objections confidently and honestly, and confirms the fit before closing.",
                mid: "Recommends a solution but the link to the customer's needs is loose, or objections are only partially addressed.",
                low: "Pushes a generic solution unrelated to the customer's needs, or dismisses or avoids objections.",
            },
            tip: "Treat objections as buying signals, because a clear, honest answer to a concern often does more to close the deal than the pitch itself.",
        },
        {
            id: "product_knowledge",
            name: "Product Knowledge",
            description: "Demonstrating accurate, confident knowledge of the product, pricing, and the value it delivers.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Explains product features, pricing, and terms accurately",
                "Articulates the value and benefits in terms relevant to the customer",
                "Compares options or plans clearly when the customer is deciding",
                "Sets realistic expectations and follows up when unsure rather than guessing",
            ],
            scoringGuide: {
                high: "Accurate and confident on product, pricing, and value, and frames the benefits around what matters to the customer.",
                mid: "Mostly accurate but vague or uncertain on a pricing detail, feature, or how the value applies to the customer.",
                low: "Provides incorrect information, cannot explain the value, or guesses rather than confirming.",
            },
            tip: "Sell the value, not just the feature, because customers buy the outcome a feature gives them, not the feature itself.",
        },
        {
            id: "tone",
            name: "Tone",
            description: "Maintaining a confident, persuasive, and professional tone that builds trust without being pushy.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Speaks with confidence and a positive, professional tone",
                "Stays calm and respectful, never pushy or aggressive, even when the customer hesitates",
                "Uses clear language free of jargon, slang, or fillers",
                "Adapts pace and style to the customer rather than reading from a script",
            ],
            scoringGuide: {
                high: "Confident, warm, and persuasive throughout, building trust and adapting naturally to the customer.",
                mid: "Professional but either flat and unconvincing or slightly pushy, and doesn't fully adapt to the customer.",
                low: "Comes across as robotic, scripted, aggressive, or unsure, undermining the customer's trust.",
            },
            tip: "Confidence sells, but pressure repels, so aim to sound assured and helpful rather than insistent.",
        },
        {
            id: "call_handling",
            name: "Call Handling",
            description: "Managing the conversation flow from opening to close, including a clear call to action and next steps.",
            defaultWeight: 20,
            goodAnswerOptions: [
                "Opens with a clear, professional introduction and sets up the conversation",
                "Guides the conversation through discovery, recommendation, and close without losing structure",
                "Makes a clear call to action or ask for the next step (e.g. a follow-up, trial, or commitment)",
                "Summarises what was agreed and confirms the next step before ending",
            ],
            scoringGuide: {
                high: "Runs a structured conversation with a strong opening, a clear call to action, and confirmed next steps.",
                mid: "Covers the main stages but the structure is loose, or the close lacks a clear next step.",
                low: "Unstructured, drifts without direction, or ends without any call to action or agreed next step.",
            },
            tip: "Always close with a clear next step, because a great conversation with no ask rarely turns into a sale.",
        },
    ],
};

// Builds the seed rubric from a category template. Each good-answer option is
// checked by default (correct_answer holds every option), and the scoring guide
// is assembled with a colon separator so the editor strips the "High score:"
// label cleanly instead of leaking it into the response text. Shared by the
// per-training-type defaults and any standalone pre-default (e.g. Superbank).
export function buildRubricFromTemplate(
    template: RubricCategoryTemplate[],
): RubricCriterion[] {
    return template.map((t) => ({
        id: t.id,
        name: t.name,
        weight: t.defaultWeight,
        description: t.description,
        correct_answer: t.goodAnswerOptions.join("\n"),
        scoring_guide: `High score: ${t.scoringGuide.high}\nMid score: ${t.scoringGuide.mid}\nLow score: ${t.scoringGuide.low}`,
        tip: t.tip,
    }));
}

export function getDefaultRubric(trainingType: TrainingType): RubricCriterion[] {
    return buildRubricFromTemplate(RUBRIC_TEMPLATES[trainingType]);
}

// Shared default rubric used to seed new training campaigns AND new AIQM
// uploads. Each instance snapshots its own copy at creation time - editing
// these templates only affects instances created *after* the change ships.
// Defaults to the Customer Service template; the campaign wizard re-seeds from
// the selected training type via getDefaultRubric().
export const DEFAULT_RUBRIC: RubricCriterion[] = getDefaultRubric("customer_service");

// Assembled, ready-to-seed copy of the Superbank live-chat rubric. Weights sum
// to 100 so it passes the editor's validity gate as-is. Pass this as the seed
// (e.g. into <RubricEditor value={...}> or the AIQM upload dialog) for this
// client instead of DEFAULT_RUBRIC.
export const SUPERBANK_LIVECHAT_RUBRIC: RubricCriterion[] =
    buildRubricFromTemplate(SUPERBANK_LIVECHAT_TEMPLATE);

// --- Attempt History ---

export interface SessionAttempt {
    attempt_number: number;
    status: SessionStatus;
    total_score: number | null;
    completed_at: string | null;
    token: string;
}

// --- Campaign Insights ---

export interface CampaignInsightsData {
    score_distribution: { bucket: string; count: number }[];
    weakest_dimension: { name: string; avg_score: number };
    top_performers: { name: string; email: string; score: number }[];
    completion_over_time: { date: string; count: number }[];
    department_breakdown: { department: string; avg_score: number; count: number }[];
}

// --- Coaching Dimensions ---

export type CoachingDimension = "handling" | "communication" | "delivery";

export const COACHING_DIMENSION_LABELS: Record<CoachingDimension, { title: string; subtitle: string }> = {
    handling: {
        title: "Issue Resolution",
        subtitle: "How well you resolved the customer's issue",
    },
    communication: {
        title: "Communication",
        subtitle: "How you connected with the customer",
    },
    delivery: {
        title: "Delivery",
        subtitle: "How you sounded",
    },
};

const HANDLING_KEYWORDS = ["problem_resolution", "product_knowledge", "call_handling", "process", "resolution", "procedure", "escalation", "compliance"];
const COMMUNICATION_KEYWORDS = ["empathy", "tone", "active_listening", "de_escalation", "rapport", "greeting", "closing"];

export function classifyRubricCriterion(name: string): CoachingDimension {
    const lower = name.toLowerCase().replace(/\s+/g, "_");
    if (HANDLING_KEYWORDS.some((k) => lower.includes(k))) return "handling";
    if (COMMUNICATION_KEYWORDS.some((k) => lower.includes(k))) return "communication";
    return "handling";
}
